import axios from 'axios';
import authService from './authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const BASE_PATH = '/api/v1/odds-data';

/**
 * Odds Data Service
 * Handles all API calls for real-time odds data (EV, Market Value, Arbs)
 */

/**
 * Get authorization headers
 */
const getAuthHeaders = () => {
    const token = authService.getToken();
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

/**
 * Handle API errors consistently
 */
const handleApiError = (error) => {
    if (error.response) {
        const { status, data } = error.response;

        // Authentication errors
        if (status === 401) {
            const errorCode = data.error?.code || 'AUTH_FAILED';
            return {
                success: false,
                status,
                errorCode,
                message: data.message || data.error?.message || 'Authentication failed',
                isAuthError: true
            };
        }

        // Subscription required
        if (status === 403) {
            return {
                success: false,
                status,
                errorCode: data.error || 'SUBSCRIPTION_REQUIRED',
                message: data.message || 'An active subscription is required to access odds data. Please upgrade your plan.',
                isSubscriptionError: true
            };
        }

        // Validation errors
        if (status === 400) {
            return {
                success: false,
                status,
                errorCode: data.error || 'VALIDATION_ERROR',
                message: data.message || 'Invalid request parameters',
                details: data.errors || []
            };
        }

        // Rate limit exceeded
        if (status === 429) {
            return {
                success: false,
                status,
                errorCode: data.error || 'RATE_LIMIT_EXCEEDED',
                message: data.message || 'Too many requests. Please try again in 15 minutes.'
            };
        }

        // Service unavailable (Redis down)
        if (status === 503) {
            return {
                success: false,
                status,
                errorCode: data.error || 'SERVICE_UNAVAILABLE',
                message: data.message || 'Odds data service is temporarily unavailable. Please try again shortly.'
            };
        }

        // Other server errors
        return {
            success: false,
            status,
            errorCode: 'SERVER_ERROR',
            message: data.error?.message || data.message || 'Server error. Please try again later.'
        };
    }

    // Network errors
    return {
        success: false,
        status: null,
        errorCode: 'NETWORK_ERROR',
        message: 'Unable to connect to server. Please check your internet connection.'
    };
};

const oddsDataService = {
    /**
     * Fetch odds data for a given sport and strategy
     * @param {string} sport - Sport identifier (e.g., 'nba')
     * @param {string} strategy - Strategy identifier ('player_line', 'market_value', 'arbs')
     * @returns {Promise} API response with odds data
     */
    getOddsData: async (sport, strategy) => {
        try {
            const response = await axios.get(`${API_URL}${BASE_PATH}`, {
                params: { sport, strategy },
                headers: getAuthHeaders()
            });

            const result = response.data;

            if (!result.success) {
                return {
                    success: false,
                    message: result.message || 'Failed to fetch odds data'
                };
            }

            return {
                success: true,
                data: result.data,
                meta: result.meta,
                message: result.message || null
            };
        } catch (error) {
            return handleApiError(error);
        }
    }
};

export default oddsDataService;
